import React, { useState } from "react";
import { formatYmd } from "../../utils/dateOnly";
import { SignatureCreator } from "../../pages/digi/NewMasterSheet/LeaseProcess/Flow/StepperComponents/lease/components/steps/components/SignatureService/SignatureCreator";

const money = (v) => `$${Number(v || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Check = ({ on }) => (
  <span aria-hidden="true" style={{
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    width: 16, height: 16, border: "1.5px solid #0c447c", borderRadius: 3,
    color: "#0c447c", fontSize: 12, lineHeight: 1, marginRight: 8, flex: "0 0 auto",
  }}>
    {on ? "✓" : ""}
  </span>
);

const HeaderRow = ({ label, value }) => (
  <div style={{ display: "flex", gap: 10, padding: "4px 0", fontSize: 14 }}>
    <span style={{ minWidth: 210, color: "#555" }}>{label}</span>
    <span style={{ flex: 1, fontWeight: 600, borderBottom: "1px dotted #c8cdd3" }}>{value || " "}</span>
  </div>
);

const SectionTitle = ({ children }) => (
  <div style={{
    fontSize: 12, fontWeight: 700, letterSpacing: "1px", color: "#0c447c",
    borderBottom: "2px solid #0c447c", paddingBottom: 5, margin: "24px 0 14px", textTransform: "uppercase",
  }}>
    {children}
  </div>
);

const RentLine = ({ label, value, strong }) => (
  <div style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid #eef0f2", fontSize: 14, fontWeight: strong ? 700 : 400 }}>
    <span>{label}</span>
    <span>{money(value)}</span>
  </div>
);

/**
 * Individual signature line. In sign mode it shows a tappable area that opens
 * the SignatureCreator dialog (Type / Draw / Upload). Each signer's link is
 * unique so only their line is interactive — matching the lease module pattern.
 */
const SignatureLine = ({ role, name, date, image, signMode, signatureValue, onSign, capturedDate }) => {
  const [open, setOpen] = useState(false);

  const handleCreated = ({ signatureData }) => {
    if (signatureData && onSign) onSign(signatureData);
  };

  return (
    <>
      {signMode && (
        <SignatureCreator
          isOpen={open}
          onClose={() => setOpen(false)}
          onSign={handleCreated}
          disableSignAndSave
        />
      )}

      <div style={{ flex: "1 1 240px", minWidth: 220 }}>
        {/* Sign-mode tap area */}
        {signMode ? (
          <div
            onClick={() => setOpen(true)}
            style={{
              minHeight: 72, borderRadius: 10, cursor: "pointer",
              border: signatureValue ? "2px solid #0f6e56" : "2px dashed #a5b4fc",
              background: signatureValue ? "#f0faf5" : "#f5f7ff",
              display: "flex", alignItems: "center", justifyContent: "center",
              marginBottom: 6, padding: "8px 12px",
              boxShadow: signatureValue
                ? "0 0 0 3px rgba(15,110,86,0.1)"
                : "0 0 0 3px rgba(99,102,241,0.08)",
              transition: "all 0.2s",
            }}
          >
            {signatureValue ? (
              <img src={signatureValue} alt="Your signature" style={{ maxHeight: 56, maxWidth: "100%", display: "block" }} />
            ) : (
              <div style={{ textAlign: "center", pointerEvents: "none" }}>
                <div style={{ fontSize: 22, marginBottom: 4 }}>✍</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#4f46e5" }}>Tap to sign</div>
                <div style={{ fontSize: 11, color: "#818cf8", marginTop: 2 }}>Type, draw, or upload</div>
              </div>
            )}
          </div>
        ) : (
          /* Read-only signature display */
          <div style={{ height: 56, display: "flex", alignItems: "flex-end", marginBottom: 6 }}>
            {image ? (
              <img src={image} alt="signature" style={{ maxHeight: 52, maxWidth: 220 }} />
            ) : date ? (
              <span style={{ color: "#0f6e56", fontSize: 12 }}>✓ Signed electronically</span>
            ) : (
              <span style={{ color: "#aaa", fontSize: 12 }}>Awaiting signature</span>
            )}
          </div>
        )}

        {/* Name / date line */}
        <div style={{ borderTop: "1px solid #333", paddingTop: 4, display: "flex", justifyContent: "space-between", fontSize: 12 }}>
          <span style={{ fontWeight: 600 }}>{name || " "}</span>
          <span style={{ color: "#666" }}>
            {signMode && signatureValue && capturedDate
              ? `Date: ${capturedDate}`
              : date
              ? `Date: ${formatYmd(date)}`
              : "Date: ____________"}
          </span>
        </div>
        <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>{role}</div>

        {/* Re-draw link once signed */}
        {signMode && signatureValue && (
          <button
            onClick={() => setOpen(true)}
            style={{
              marginTop: 6, fontSize: 11, color: "#4f46e5", background: "none",
              border: "none", cursor: "pointer", padding: 0, textDecoration: "underline",
            }}
          >
            Re-draw signature
          </button>
        )}
      </div>
    </>
  );
};

/* ─── Main document component ────────────────────────────────────────────── */
const AddendumDocument = ({ addendum = {}, signTenantIndex = null, signatureValue = "", onSign, capturedDate = "" }) => {
  const ad = addendum.amendment_details || {};
  const rr = addendum.revised_rent || {};
  const tenantSigs = addendum.tenant_signatures || [];
  const landlord = addendum.landlord_signature || {};

  return (
    <div style={{
      maxWidth: 760, margin: "0 auto", background: "#fff", border: "1px solid #e6e9ee",
      borderRadius: 10, boxShadow: "0 1px 4px rgba(16,42,80,0.06)", padding: "40px 48px",
      fontFamily: "Georgia, 'Times New Roman', serif", color: "#1a1a1a",
    }}>
      <div style={{ textAlign: "center", marginBottom: 8 }}>
        <h1 style={{ fontSize: 24, letterSpacing: "0.5px", margin: 0 }}>Lease Addendum Agreement</h1>
        {addendum.addendum_id && (
          <div style={{ color: "#888", fontSize: 12, marginTop: 4 }}>Reference: {addendum.addendum_id}</div>
        )}
      </div>
      <div style={{ borderTop: "2px solid #0c447c", margin: "12px 0 20px" }} />

      <HeaderRow label="Property Address:" value={addendum.property_address} />
      <HeaderRow label="Tenant(s):" value={addendum.tenant_names} />
      <HeaderRow label="Original Lease Date:" value={addendum.original_lease_date ? formatYmd(addendum.original_lease_date) : ""} />
      <HeaderRow label="Effective Date of Amendment:" value={addendum.effective_date ? formatYmd(addendum.effective_date) : ""} />

      <p style={{ fontSize: 13.5, lineHeight: 1.7, color: "#333", marginTop: 18 }}>
        This Addendum forms part of and amends the Residential Tenancy Agreement for the above-noted premises. Except as
        amended herein, all other terms and conditions of the original lease agreement remain unchanged and in full force and effect.
      </p>

      <SectionTitle>Amendment Details</SectionTitle>
      <div style={{ fontSize: 14, lineHeight: 1.8 }}>
        <div style={{ display: "flex", alignItems: "center", fontWeight: 600 }}>
          <Check on={!!ad.parking?.selected} /> Parking Added / Removed
        </div>
        {ad.parking?.selected && (
          <div style={{ paddingLeft: 24, color: "#333" }}>
            New Monthly Parking Charge: <strong>{money(ad.parking.new_monthly_charge)}</strong>
            {ad.parking.action ? ` (${ad.parking.action})` : ""}
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", fontWeight: 600, marginTop: 8 }}>
          <Check on={!!ad.pet?.selected} /> Pet Fee Added / Removed
        </div>
        {ad.pet?.selected && (
          <div style={{ paddingLeft: 24, color: "#333" }}>
            Pet Description: <strong>{ad.pet.description || "—"}</strong>
            {ad.pet.action ? ` (${ad.pet.action})` : ""}<br />
            Non-Refundable Pet Fee: <strong>{money(ad.pet.non_refundable_fee)}</strong><br />
            New Monthly Pet Fee: <strong>{money(ad.pet.new_monthly_fee)}</strong>
          </div>
        )}

        <div style={{ display: "flex", alignItems: "center", fontWeight: 600, marginTop: 8 }}>
          <Check on={(ad.others || []).length > 0} /> Other Amendment — Storage / Telus
        </div>
        {(ad.others || []).length > 0 && (
          <div style={{ paddingLeft: 24, color: "#333" }}>
            {ad.others.map((o, i) => (
              <div key={i}>{o.label || "Other"}: <strong>{money(o.amount)}</strong></div>
            ))}
          </div>
        )}
      </div>

      <SectionTitle>Revised Monthly Rent</SectionTitle>
      <div>
        <RentLine label="New Base Rent" value={rr.new_base_rent} />
        <RentLine label="New Parking Charge" value={rr.new_parking_charge} />
        <RentLine label="New Pet Fee" value={rr.new_pet_fee} />
        <RentLine label="New Storage Fee" value={rr.new_storage_fee} />
        <RentLine label="New Other Charges" value={rr.new_other_charges} />
        <div style={{
          marginTop: 6, padding: "10px 12px", background: "#f1f6fc", borderRadius: 6,
          display: "flex", justifyContent: "space-between", fontSize: 15, fontWeight: 700, color: "#0c447c",
        }}>
          <span>New Total Monthly Rent Payable</span>
          <span>{money(rr.new_total_monthly_rent)}</span>
        </div>
      </div>

      <p style={{ fontSize: 13.5, lineHeight: 1.7, color: "#333", marginTop: 22 }}>
        The Tenant(s) and Landlord acknowledge and agree to the above amendment. This Addendum shall become part of the
        existing Residential Tenancy Agreement effective on the date noted above.
      </p>

      <SectionTitle>Signatures</SectionTitle>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 28, marginTop: 8 }}>
        {tenantSigs.map((t) => (
          <SignatureLine
            key={t.tenant_index}
            role="Tenant Signature"
            name={t.name}
            date={t.date}
            image={t.signature}
            signMode={signTenantIndex === t.tenant_index}
            signatureValue={signatureValue}
            onSign={onSign}
            capturedDate={capturedDate}
          />
        ))}
        <SignatureLine
          role="Landlord / Property Manager"
          name={landlord.name}
          date={landlord.date}
          image={landlord.signature}
        />
      </div>
    </div>
  );
};

export default AddendumDocument;
